import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { ConstructorApi } from '@/features/constructor/api';
import { modifyAnswers } from '@/features/constructorCommon/utils';
import { queryClient } from '@/app/QueryClientProvider';
import { usePopups } from '@/hooks';

export const useQuestionnaireGroup = ({ questionnaireId }) => {
  const { t } = useTranslation();
  const { showErrorToast, showSuccessToast } = usePopups();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = useCallback(async ({ group, sectionId, dAnswersProp }) => {
    try {
      setIsCreating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : group.visibleConditions;
      const result = await ConstructorApi.createGroup({ ...group, visibleConditions }, sectionId);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionnaireId
            })
        });
        showSuccessToast('Created');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsCreating(false);
    }
  }, []);

  const handleUpdate = useCallback(async ({ group, dAnswersProp }) => {
    try {
      setIsUpdating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : group.visibleConditions;
      const result = await ConstructorApi.updateGroup({ ...group, visibleConditions });
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionnaireId
            })
        });
        showSuccessToast('Saved');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (group) => {
    try {
      setIsDeleting(true);
      const result = await ConstructorApi.deleteGroup(group);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionnaireId
            })
        });
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    handleCreate,
    handleUpdate,
    handleDelete,
    isCreating,
    isUpdating,
    isDeleting
  };
};
